import { FC, useCallback, useState } from 'react';
import { Button, Divider, RadioGroup, RadioGroupItem } from '@features/ui';
import { notesRadio } from '@features/admin/users/usersData.ts';
import { formatDateByHours } from '@lib/utils.ts';
import { AddNotePopup } from '@features/admin/users/AddNotePopup.tsx';
import { UserNote, Notes as TNotes } from '@src/types/users.ts';
import {
  addNote,
  updateNote,
  deleteNote,
  getNotes,
} from '@src/services/users.ts';
import { useAuthStore } from '@src/stores/authStore';
import { canAccess, canEditOwnNote } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';

interface NotesProps {
  userId: number;
  notes: UserNote[];
  filterNotes: (notes: UserNote[]) => void;
  refreshNotes: () => Promise<void>;
}
export const Notes: FC<NotesProps> = ({
  userId,
  notes,
  filterNotes,
  refreshNotes,
}) => {
  const { auth } = useAuthStore();
  const [noteFilterSelected, setNoteFilterSelected] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [noteSelected, setNoteSelected] = useState<UserNote | null>(null);

  const handleEdit = useCallback((item: UserNote) => {
    setNoteSelected(item);
    setOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (item: UserNote) => {
      try {
        await deleteNote(userId, item.id);
        await refreshNotes();
      } catch (error) {
        console.error('Failed to delete note:', error);
      }
    },
    [userId, refreshNotes]
  );

  const handleConfirm = useCallback(
    async (value: string) => {
      await new Promise((resolve) => setTimeout(resolve, 500));
      if (!noteSelected) {
        await addNote(userId, value);
      } else {
        await updateNote(userId, noteSelected.id, value);
      }
      await refreshNotes();
      setNoteSelected(null);
    },
    [noteSelected, refreshNotes, userId]
  );

  const renderContent = (content?: string | null) => {
    if (!content) return null;
    if (/<[a-z][\s\S]*>/i.test(content)) {
      return <div dangerouslySetInnerHTML={{ __html: content }} />;
    } else {
      return <div>{content}</div>;
    }
  };

  const handleFilterNotes = useCallback(
    async (val: TNotes) => {
      setNoteFilterSelected(val);
      try {
        const searchWords = val === TNotes.ALl ? '' : val;
        const response = await getNotes(userId, searchWords);

        filterNotes(response.data);
      } catch (error) {
        console.error('Failed to filter notes:', error);
      }
    },
    [userId, filterNotes]
  );

  const canEditNote = (userId: number) => {
    return (
      canEditOwnNote(
        UserRolePermission.EDIT_OWN_NOTE,
        auth?.user?.permissions,
        userId,
        auth?.user?.id
      ) || canAccess(UserRolePermission.EDIT_ANY_NOTE, auth?.user?.permissions)
    );
  };

  return (
    <>
      <div
        className={
          'lg:absolute lg:left-110 lg:right-0 lg:top-2 text-sm flex lg:flex-row flex-col justify-between lg:items-center items-start lg:gap-0 gap-4 lg:mb-0 mb-4'
        }
      >
        <RadioGroup
          className={'flex item-center gap-x-8.5'}
          defaultValue={noteFilterSelected}
          onValueChange={handleFilterNotes}
        >
          {notesRadio.map((item) => {
            return (
              <div key={item.value} className={'flex items-center gap-x-2.5'}>
                <RadioGroupItem value={item.value} />
                <p>{item.label}</p>
              </div>
            );
          })}
        </RadioGroup>
        {canAccess(UserRolePermission.ADD_NOTE, auth?.user?.permissions) && (
          <Button
            size={'sm'}
            variant={'yellow'}
            onClick={() => {
              setNoteSelected(null);
              setOpen(true);
            }}
          >
            Add Note
          </Button>
        )}
      </div>
      <Divider />
      <div className={'flex flex-col'}>
        {notes.map((item) => (
          <div key={item.id} className={'mt-[13px]'}>
            <div
              className={'flex justify-between items-center text-xs mb-[17px]'}
            >
              <div className={'flex flex-col gap-[2px]'}>
                <p className={'text-blue'}>
                  {formatDateByHours(item.created_at, true)} by{' '}
                  {item?.user ? item?.user?.fullname : 'System'}
                </p>
                {renderContent(item.content)}
              </div>
              <div className={'flex gap-x-4'}>
                {canAccess(
                  UserRolePermission.DELETE_NOTE,
                  auth?.user?.permissions
                ) && (
                  <Button
                    className={'bg-[#C13839]'}
                    size={'sm'}
                    onClick={() => handleDelete(item)}
                  >
                    Delete
                  </Button>
                )}
                {canEditNote(item.user_id) && (
                  <Button
                    size={'sm'}
                    variant={'black'}
                    onClick={() => handleEdit(item)}
                  >
                    Edit
                  </Button>
                )}
              </div>
            </div>
            <Divider />
          </div>
        ))}
      </div>
      <AddNotePopup
        noteEdit={noteSelected?.content}
        open={open}
        onConfirm={handleConfirm}
        onOpenChange={setOpen}
      />
    </>
  );
};
